import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Chip,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  useTheme,
  alpha,
  Badge,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  getExportProductById,
  getProductionsByProductId,
  getAllMaterialsByBarcodeId,
  getLast5ProductionsByBarcode,
} from "../../../services/Export";
import TableItem from "../../../components/TableItem";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import AssessmentIcon from "@mui/icons-material/Assessment";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import BuildIcon from "@mui/icons-material/Build";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function WarehouseProductView() {
  const theme = useTheme();
  const history = useHistory();
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [productionList, setProductionList] = useState([]);
  const [productionMaterials, setProductionMaterials] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const result = await getExportProductById(productId);

      if (result && result.product_id) {
        setProduct(result);

        // Fetch production materials (BOM) from ProductBarcode
        if (result.barcodeId && result.barcode !== "") {
          const materials = await getAllMaterialsByBarcodeId(result.barcodeId);
          if (materials && materials !== "Something went wrong...") {
            setProductionMaterials(materials);
          }
        }

        // Get production data for this warehouse
        const productionSet = await getLast5ProductionsByBarcode(
          result.barcode,
        );
        setProductionList(productionSet);

        // Format data for table display
        if (productionSet && productionSet.length > 0) {
          const formattedRows = productionSet.map((production) => ({
            date: formatDate(production.productionDate),
            productionQty: production.productionQty,
            totalCost: production.totalCost,
            totalItemCost: production.totalItemCost,
            electricityCost: production.electricityCost,
            labourCost: production.labourCost,
            transportCost: production.transportCost,
          }));

          setRows(formattedRows);
          setTotalPages(Math.ceil(formattedRows.length / 10));
        } else {
          setRows([]);
          setTotalPages(0);
        }
      }
    } catch (error) {
      console.error("Error fetching product data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [productId]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "Rs: 0.00";

    return typeof value === "number"
      ? `Rs: ${value.toFixed(2)}`
      : `Rs: ${parseFloat(value || 0).toFixed(2)}`;
  };

  // Table columns with improved formatting
  const columns = [
    {
      id: "date",
      label: "Production Date",
      minWidth: 100,
    },
    {
      id: "productionQty",
      label: "Production Qty",
      minWidth: 100,
      align: "center",
    },
    {
      id: "totalCost",
      label: "Total Cost",
      minWidth: 100,
      format: (value) => formatCurrency(value),
    },
    {
      id: "totalItemCost",
      label: "Material Cost",
      minWidth: 100,
      format: (value) => formatCurrency(value),
    },
    {
      id: "electricityCost",
      label: "Electricity Cost",
      minWidth: 120,
      format: (value) => formatCurrency(value),
    },
    {
      id: "labourCost",
      label: "Labour Cost",
      minWidth: 100,
      format: (value) => formatCurrency(value),
    },
    {
      id: "transportCost",
      label: "Transport Cost",
      minWidth: 100,
      format: (value) => formatCurrency(value),
    },
  ];

  const handleViewMasterProduct = () => {
    if (product && product.productBarcode && product.productBarcode.barcodeId) {
      history.push(`/template/product/${product.productBarcode.barcodeId}`);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading warehouse product details...
        </Typography>
      </Box>
    );
  }

  if (!product || !product.product_id) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No product data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        m: { xs: 1, md: 3 },
        borderRadius: 2,
        bgcolor: theme.palette.mode === "dark"
          ? theme.palette.background.paper
          : "#f9fafb",
      }}
    >
      {/* Header with close button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <WarehouseIcon fontSize="large" color="primary" />
          <Box>
            <Typography variant="h5" fontWeight="600" color="primary.main">
              Warehouse Product Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Viewing warehouse-specific inventory and production
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Close">
            <IconButton
              onClick={() => history.goBack()}
              aria-label="close"
              sx={{
                bgcolor: alpha(theme.palette.grey[500], 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.grey[500], 0.2),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Product Information */}
      <Grid container spacing={2}>
        {/* Warehouse & Product Details */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <InventoryIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Product & Warehouse Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Product Name
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {product.name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Barcode
                  </Typography>
                  <Typography variant="body1">{product.barcode}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={product.status}
                    color={product.status === "ACTIVE" ? "success" : "error"}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      <WarehouseIcon
                        sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
                      />
                      Warehouse
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {product.wareHouse?.warehouseName || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Type: {product.wareHouse?.warehouseType || "N/A"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Stock Qty (This Warehouse)
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    <Chip
                      label={product.existingQty}
                      color={
                        parseInt(product.existingQty) > 10
                          ? "success"
                          : "warning"
                      }
                      size="small"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added By
                  </Typography>
                  <Typography variant="body1">{product.addedBy}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Current Price & Stats */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AssessmentIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Warehouse Statistics
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Current Price
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      color="primary.main"
                    >
                      {product.currentPrice
                        ? formatCurrency(product.currentPrice)
                        : "Not set"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total Production (Here)
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      color="info.main"
                    >
                      {productionList.reduce(
                        (sum, item) =>
                          sum + (parseInt(item.productionQty) || 0),
                        0,
                      )}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Available Qty
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      color="success.main"
                    >
                      {product.existingQty}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Production Batches
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      color="warning.main"
                    >
                      {productionList.length}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Categories */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CategoryIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Product Categories
                </Typography>
              </Box>

              {product?.ex_categories?.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {product.ex_categories.map((category, index) => (
                    <Chip
                      key={index}
                      label={
                        <Typography variant="body2">
                          <span style={{ fontWeight: "600" }}>
                            {category.category}
                          </span>
                          : {category.subCat_1}
                        </Typography>
                      }
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: 1,
                        py: 0.5,
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">
                  No categories available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Production Materials (BOM) */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <BuildIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="primary.main"
                  >
                    Production Materials (BOM)
                  </Typography>
                </Box>
                <Tooltip title="Manage Materials">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      if (
                        product.productBarcode &&
                        product.productBarcode.barcodeId
                      ) {
                        history.push(
                          `/template/product/${product.productBarcode.barcodeId}/materials`,
                        );
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {productionMaterials?.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {productionMaterials.map((material, index) => (
                    <Chip
                      key={index}
                      label={
                        <Typography variant="body2">
                          <span style={{ fontWeight: "600" }}>
                            {material.materialName}
                          </span>
                          : {material.standardQtyPerUnit}{" "}
                          {material.materialUnitType || "units"} per unit
                        </Typography>
                      }
                      sx={{
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        borderRadius: 1,
                        py: 0.5,
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    No materials defined yet
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<BuildIcon />}
                    onClick={() => {
                      if (
                        product.productBarcode &&
                        product.productBarcode.barcodeId
                      ) {
                        history.push(
                          `/template/product/${product.productBarcode.barcodeId}/materials`,
                        );
                      }
                    }}
                  >
                    Add Materials (BOM)
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Production History Table */}
        <Grid item xs={12}>
          <Card elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ProductionQuantityLimitsIcon
                  sx={{ mr: 1, color: "primary.main" }}
                />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Production History (This Warehouse)
                </Typography>
                <Badge
                  badgeContent={rows.length}
                  color="primary"
                  sx={{ ml: 1 }}
                />
              </Box>

              {rows.length > 0 ? (
                <Box
                  sx={{
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <TableItem
                    columns={columns}
                    rows={rows}
                    page={page}
                    setPage={setPage}
                    tablePagin={true}
                    totalPages={totalPages}
                    getData={fetchData}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.grey[500], 0.05),
                    borderRadius: 1,
                  }}
                >
                  <Typography color="text.secondary">
                    No production history available for this warehouse
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default WarehouseProductView;
